import { SearchParams } from './common'
import { Controller } from './controller'
import { Emitter } from './emitter'
import { EtZone } from './etZone'
import { Floor } from './floor'
import { Pipe } from './pipe'
import { Plant } from './plant'
import { Soil } from './soil'
import { MARKER_TYPE } from './marker'

export interface Station {
  id: string
  controller: string
  decoderType: string
  state: string
  plant: string
  soil: string
  errorName: string
  errorCode: number
  emitter: string
  etRuntime: number
  interfaceId: number
  index: string
  address: string
  type: string
  defaultRunTime: string
  pipe: {
    id: number
    interfaceId: number
    description: string
    expectedFlow: number
    measuredFlow: number
    observedIdleFlow: number
  }
  etZone: {
    id: number
    interfaceId: number
    name: string
    etNumber: string | null
  }
  waterRequired: number
  surfaceAccumulation: number
  plantAvailWater: number | null
  precipitationRate: number
  expectedFlow: number
  observedFlow: number
  landscapeFactor: number
  rtDayOverride: number | null
  subArea: SUB_AREA | null
  priority: string | null
  stationType?: number | null
  decoderId?: string
}

export interface ApplyObservedFlow {
  interfaceId: number
  stationIds: number[]
}

export interface CreateStation {
  interfaceId: number
  pictureUrl: string
  pictureFileName: string
  priority: number
  description?: string
  grsCoordinatedLatitude?: number | null
  grsCoordinatedLongitude?: number | null
  floorId?: number | null
  // Null on a non-irrigation station: it has no water requirement, no flow and
  // no run time to report, and 0 would claim it had measured them as none.
  surfaceAccumulation: number | null
  waterRequired: number | null
  plantAvailWater: number | null
  precipitationRate: number | null
  expectedFlow: number | null
  landscapeFactor: number | null
  rtDayOverride: number | null
  decoderId?: string
  // DECISION: the decoder tuple stays optional next to decoderId, not removed, because a backend without vnv-cisgenics-BE#405 still reads it. DEPRECATED SOON: remove once #405 is on production (SCRUM-1169).
  index?: number
  address?: number
  // Null where the station has none: a non-irrigation station has no agronomy,
  // and 0 is a foreign key matching no row.
  soilId: number | null
  plantId: number | null
  pipeId?: number 
  emitterId: number | null
  etZoneId: number | null
  stationGroupId: number
  subAreaId: number
  /** Required by the server, zero on a station that does not run. */
  defaultRunTime: string
  twiId?: number | null
  Type?: number | null
}

export interface UpdateStation {
  interfaceId: number
  id: number
  pictureUrl: string
  pictureFileName: string
  priority: number
  description?: string
  grsCoordinatedLatitude?: number | null
  grsCoordinatedLongitude?: number | null
  floorId?: number | null
  /** See `CreateStation` — null on a non-irrigation station. */
  surfaceAccumulation: number | null
  waterRequired: number | null
  plantAvailWater: number | null
  precipitationRate: number | null
  expectedFlow: number | null
  landscapeFactor: number | null
  rtDayOverride: number | null
  decoderId?: string
  index?: number
  address?: number
  /** See `CreatePlant.plantId` — null where the station has none. */
  plantId: number | null
  pipeId?: number | null
  soilId: number | null
  emitterId: number | null
  etZoneId: number | null
  stationGroupId: number
  /** Required by the server, zero on a station that does not run. */
  defaultRunTime: string
  subAreaId: number
  twiId?: number | null
  Type?: number | null
}

export interface SearchStation extends SearchParams {
  interfaceId: number
  keyword?: string
  floorId?: number
  /**
   * SCRUM-1071 column filters. Each repeats in the query string to OR within a
   * column; different ones AND across columns, and `totalCount` reflects the
   * result. Built by `buildStationServerFilters`.
   */
  AreaIds?: number[]
  SubAreaIds?: number[]
  PlantIds?: number[]
  SoilIds?: number[]
  EmitterIds?: number[]
  PipeIds?: number[]
  StationTypeIds?: number[]
  /** Station state ids — the enum `STATION_STATUS_NAME` maps. */
  StateIds?: number[]
  StationIds?: number[]
}

export interface SoilMoistureDetail {
  salinity_EC: number
  soil_Moisture: number
  temperature: number
  thirdPartySensorRef: number
  timeSpan: string
}

export interface SetupStation {
  id: number
  interfaceId: number
  controller: string
  decoderType: string
  state: string
  plant: string
  emitter: string
  etRuntime: number
  defaultRunTime: string
  address: number
  index: number
  type: string
  priority: number
  configure: string
  soil: string
  pipe: string
  plantGroup: string
  emitterEfficiency: string
  etZone: string
  waterRequired: number
  surfaceAccumulation: number
  plantAvailWater: number
  precipitationRate: number
  expectedFlow: number
  landscapeFactor: number
  rtDayOverride: number
  siteId: number | null
  siteName: string | null
  locationId: number | null
  locationName: string | null
  floorId: number | null
  grsCoordinatedLatitude: number
  grsCoordinatedLongitude: number
  areaId: number | null
  areaName: string | null
  subAreaId: number | null
  subAreaName: string | null
  description: string
  marker: {
    id: number
    name: string
    imageUrl: string
    s3Url: string
  }
  markertType?: MARKER_TYPE // Only define from front-end when use create icon market in map
}

export interface SoilMoisture {
  id: number
  grsCoordinatedLatitude: number | null
  grsCoordinatedLongitude: number | null
  description: string
  floorId: number | null
  marker: {
    id: number
    name: string
    imageUrl: string
    s3Url: string
  }
}

export interface FlowSensor {
  id: number
  grsCoordinatedLatitude: number | null
  grsCoordinatedLongitude: number | null
  address: number
  index: number
  description: string
  floorId: number | null
  marker: {
    id: number
    name: string
    imageUrl: string
    s3Url: string
  }
}
export interface STATION_DETAIL {
  id: number
  decoderId?: string | null
  pictureUrl: string
  pictureFileName: string
  priority: number
  grsCoordinatedLatitude: number
  grsCoordinatedLongitude: number
  waterRequired: number
  surfaceAccumulation: number
  plantAvailWater: number
  precipitationRate: number
  expectedFlow: number
  landscapeFactor: number
  rtDayOverride: number
  lastUpdate: string
  lastETUpdate: string
  controller: Controller
  emitter: Emitter
  soil: Soil
  plant: Plant
  pipe: Pipe
  etZone: EtZone
  floor: Floor
  siteId: number | null
  siteName: string | null
  locationId: number | null
  locationName: string | null
  defaultRunTime: string
  errorCode: number
  isRunning: boolean
  isScheduled: boolean
  remainingTime: number
  totalCycleTime: number
  totalSoakTime: number
  totalIrrigationTime: number
  startTime: string
  endTime: string
  subArea: {
    interfaceId: number
    id: number
    name: string
    parentId: number
    level: number
    parentArea: {
      id: number
      interfaceId: number
      name: string
      parentArea: null
    }
  }
  active: boolean
  stateName: string
  description: string
  index: number
  address: number
  type: string
  floorId: number | null
  plantId: number
  soilId: string
  pipeId: number
  emitterId: number | null
  etZoneId: number | null
  stationGroupId: number
  twiId: number
  stationType?: number | null
}

export interface SUB_AREA {
  id: number
  name: string
  parentArea: AREA
}

export interface AREA {
  id: number
  name: string
}

export const STATION_DELAY_ID_DEFAULT = 22000
